import { type ReactNode, useEffect } from 'react';
import { ChevronLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';

type LegalDocumentLayoutProps = {
  title: string;
  description: string;
  children: ReactNode;
};

const LegalDocumentLayout = ({ title, description, children }: LegalDocumentLayoutProps) => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = `DwellBot Games | ${title}`;
    return () => {
      document.title = previousTitle;
    };
  }, [title]);

  return (
    <main className="relative min-h-full overflow-hidden bg-[#040712] text-white">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(248,196,113,0.16),transparent_34%),linear-gradient(180deg,rgba(8,13,28,0.96),rgba(3,6,16,1))]" />
      <div className="absolute left-[-6rem] top-28 h-72 w-72 rounded-full bg-cyan-400/10 blur-3xl" />
      <div className="absolute right-[-6rem] top-20 h-80 w-80 rounded-full bg-[#f59e0b]/12 blur-3xl" />
      <div className="absolute bottom-[-8rem] left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-fuchsia-500/10 blur-3xl" />
      <div className="relative z-10 mx-auto flex min-h-full w-full max-w-5xl flex-col px-4 pb-12 pt-24 sm:px-6 sm:pb-16 sm:pt-12 lg:px-8">
        <div className="mb-6">
          <Link
            className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/5 px-4 py-2 text-sm font-medium text-white/80 transition hover:border-white/20 hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#040712]"
            to="/"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to home
          </Link>
        </div>
        <header className="mb-8 max-w-3xl">
          <Badge className="border-white/20 bg-white/10 text-white">Public legal document</Badge>
          <h1 className="mt-4 text-4xl font-semibold tracking-tight text-white sm:text-5xl">{title}</h1>
          <p className="mt-4 max-w-2xl text-base text-white/70 sm:text-lg">{description}</p>
        </header>
        <article className="legal-document rounded-[2rem] border border-white/12 bg-black/30 p-6 shadow-[0_24px_80px_rgba(0,0,0,0.45)] backdrop-blur-md sm:p-10">
          {children}
        </article>
      </div>
    </main>
  );
};

export default LegalDocumentLayout;
